import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, useRouteMatch } from 'react-router-dom';
import { ApplicationState } from '../../../../store';
import { ILongSteelOrderItem } from '../../../../store/ducks/long-steel-shopping-cart/types';
import { saveUser } from '../../../../store/ducks/user/actions';
import {
  ECodeType,
  ESpreadsheetField,
  EUserPreferenceType,
  IOrderEntryPreference,
  ISaveUser
} from '../../../../store/ducks/user/types';
import Checkout from '../Checkout';
import EntryType from './EntryType';
import RepeatOrder from './RepeatOrder';
import SearchItem from './SearchItem';
import ShoppingCartTable from './ShoppingCartTable';
import SpreadsheetUpload from './SpreadsheetUpload';

const orderEntryPreferenceDefault: IOrderEntryPreference = {
  name: EUserPreferenceType.ORDER_ENTRY,
  codeType: ECodeType.SKU,
  spreadsheetLine: 3,
  spreadsheetColumns: [
    {
      key: 0,
      field: ESpreadsheetField.SKU,
      label: 'Código da Gerdau',
      position: 'A'
    },
    {
      key: 1,
      field: ESpreadsheetField.CLIENT_SKU,
      label: 'Meu código interno',
      position: ''
    },
    {
      key: 2,
      field: ESpreadsheetField.QUANTITY,
      label: 'Quantidade',
      position: 'B'
    },
    { key: 3, field: ESpreadsheetField.UNITY, label: 'Unidade', position: 'C' },
    {
      key: 4,
      field: ESpreadsheetField.DESIRED_DATE,
      label: 'Data desejada',
      position: 'D'
    },
    {
      key: 5,
      field: ESpreadsheetField.CUSTOMER_PURCHASE_ITEM,
      label: 'Item do pedido',
      position: 'E'
    }
  ]
};

export enum EOrderSettingsRoutes {
  ENTRY_TYPE = 'entry-type',
  SEARCH_ITEMS = 'search-items',
  SPREADSHEET_UPLOAD = 'spreadsheet-upload',
  SHOPPING_CART = 'shopping-cart',
  REPEAT_ORDER = 'repeat-order',
  CHECKOUT = 'checkout'
}

interface OrderSettingsRoutesProps {
  items: ILongSteelOrderItem[];
  onUpdateCartItem(): void;
  onRemoveItem(idxsToRemove: number[]): void;
  numberMaxOfItens: number;
}

const OrderSettingsRoutes: React.FC<OrderSettingsRoutesProps> = ({
  items,
  onUpdateCartItem,
  onRemoveItem,
  numberMaxOfItens
}) => {
  const { path } = useRouteMatch();
  const emitterId = useSelector(
    (state: ApplicationState) => state.longSteelShoppingCart
  ).emitterId;

  const dispatch = useDispatch();

  const getUserOrderEntryPreference = (): IOrderEntryPreference => {
    const orderEntryIdx = user.preferences.findIndex(
      (preference) => preference.name === EUserPreferenceType.ORDER_ENTRY
    );

    if (
      orderEntryIdx !== -1 &&
      (user.preferences[orderEntryIdx] as IOrderEntryPreference)
        .spreadsheetColumns
    ) {
      const orderEntryPreferences = user.preferences[
        orderEntryIdx
      ] as IOrderEntryPreference;

      if (
        orderEntryPreferences.spreadsheetColumns.length <
        orderEntryPreferenceDefault.spreadsheetColumns.length
      ) {
        orderEntryPreferenceDefault.spreadsheetColumns.forEach((col) => {
          if (
            !orderEntryPreferences.spreadsheetColumns.find(
              (item) => item.field === col.field
            )
          ) {
            orderEntryPreferences.spreadsheetColumns.push({
              ...col,
              position: ''
            });
          }
        });
      }

      return orderEntryPreferences;
    } else {
      return orderEntryPreferenceDefault;
    }
  };

  const user = useSelector((state: ApplicationState) => state.user);
  const [userOrderEntryPreference, setUserOrderEntryPreference] =
    useState<IOrderEntryPreference>(getUserOrderEntryPreference());

  const updateUserOrderEntryPreference = (
    newUserOrderEntryPreference: IOrderEntryPreference
  ) => {
    const saveUserProps: ISaveUser = {
      user: user,
      userPreference: newUserOrderEntryPreference
    };
    setUserOrderEntryPreference(newUserOrderEntryPreference);
    dispatch(saveUser(saveUserProps));
  };

  return (
    <>
      <Route path={`${path}/`} exact>
        <Redirect to={`${path}/${EOrderSettingsRoutes.ENTRY_TYPE}`} />
      </Route>

      <Route
        path={`${path}/${EOrderSettingsRoutes.ENTRY_TYPE}`}
        render={() => <EntryType emitterId={emitterId} />}
      />

      <Route
        path={`${path}/${EOrderSettingsRoutes.REPEAT_ORDER}`}
        render={() => <RepeatOrder customerId={emitterId}></RepeatOrder>}
      />
      <Route
        path={`${path}/${EOrderSettingsRoutes.SEARCH_ITEMS}`}
        render={() => (
          <SearchItem
            emitterId={emitterId}
            userOrderEntryPreference={userOrderEntryPreference}
            updateUserOrderEntryPreference={updateUserOrderEntryPreference}
          />
        )}
      />

      <Route
        path={`${path}/${EOrderSettingsRoutes.SPREADSHEET_UPLOAD}`}
        render={() => (
          <SpreadsheetUpload
            emitterId={emitterId}
            userOrderEntryPreference={userOrderEntryPreference}
            updateUserOrderEntryPreference={updateUserOrderEntryPreference}
          />
        )}
      />

      <Route
        path={`${path}/${EOrderSettingsRoutes.SHOPPING_CART}`}
        render={() => (
          <ShoppingCartTable
            items={items}
            emitterId={emitterId}
            onUpdateCartItem={() => onUpdateCartItem()}
            onRemoveItem={(idxsToRemove) => onRemoveItem(idxsToRemove)}
            numberMaxOfItens={numberMaxOfItens}
          />
        )}
      />

      <Route
        path={`${path}/${EOrderSettingsRoutes.CHECKOUT}`}
        component={Checkout}
      />
    </>
  );
};

export default OrderSettingsRoutes;
